import { Input } from '@mui/base/Input';
import cx from 'classnames';
import type {
  Control,
  FieldPath,
  FieldValues,
  PathValue,
  UseControllerProps,
} from 'react-hook-form';
import { useController } from 'react-hook-form';
import typography from '~styles/typography.scss';
import styles from './ControlledFormPercentageInput.scss';
import { FormTooltip } from './FormTooltip';
import { LayoutVariant } from './sharedTypes';
import { ValidationErrorMessage } from './ValidationErrorMessage';

const MIN_PERCENT = 0;
const MAX_PERCENT = 100;

export interface ControlledFormPercentageInputProps<
  T extends FieldValues = FieldValues,
  Name extends FieldPath<T> = FieldPath<T>,
> {
  className?: string;
  control: Control<T>;
  defaultValue?: PathValue<T, Name>;
  disabled?: boolean;
  label: string;
  name: Name;
  placeholder?: string;
  rules?: UseControllerProps<T, Name>['rules'];
  tooltipText?: string;
  variant?: LayoutVariant;
  labelClassName?: string;
  darkMode?: boolean;
  step?: number;
}

export const ControlledFormPercentageInput = <
  T extends FieldValues = FieldValues,
  Name extends FieldPath<T> = FieldPath<T>,
>({
  className,
  control,
  defaultValue,
  disabled,
  label,
  name,
  placeholder,
  rules = {},
  tooltipText,
  variant = LayoutVariant.Vertical,
  labelClassName = '',
  darkMode = false,
  step,
}: ControlledFormPercentageInputProps<T, Name>) => {
  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({
    control,
    defaultValue,
    name,
    rules: {
      min: { value: MIN_PERCENT, message: `Must be at least ${MIN_PERCENT}` },
      max: { value: MAX_PERCENT, message: `Must be at most ${MAX_PERCENT}` },
      ...rules,
    },
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    if (raw === '') {
      onChange('');
      return;
    }
    const parsed =
      step === 1 ? Number.parseInt(raw, 10) : Number.parseFloat(raw);
    if (Number.isNaN(parsed)) {
      return;
    }
    const clamped = Math.min(MAX_PERCENT, Math.max(MIN_PERCENT, parsed));
    onChange(clamped === parsed ? parsed : clamped);
  };

  const displayValue = value === undefined || value === '' ? '' : String(value);

  const percentAdornment = (
    <span className={styles.percent} aria-hidden="true">
      %
    </span>
  );

  return (
    <div
      className={cx(styles.fieldContainer, styles[variant], className, {
        [styles.darkMode]: darkMode,
      })}
    >
      <div className={styles.labelContainer}>
        <label
          className={cx({
            [typography.c2_20]: true,
            [styles.labelError]: !!error,
            [labelClassName]: !!labelClassName,
          })}
          htmlFor={name}
        >
          {label}
        </label>
        <FormTooltip label={name} text={tooltipText} />
      </div>
      <div className={styles.inputContainer}>
        <Input
          autoComplete="off"
          disabled={disabled}
          id={name}
          onChange={handleChange}
          onWheel={() => (document.activeElement as HTMLInputElement).blur()}
          placeholder={placeholder}
          slotProps={{
            root: {
              className: cx(typography.t1, styles.inputRoot, {
                [styles.inputError]: !!error,
              }),
            },
            input: {
              'aria-errormessage': `${name}-error`,
              'aria-invalid': !!error,
              'aria-valuemin': MIN_PERCENT,
              'aria-valuemax': MAX_PERCENT,
              max: MAX_PERCENT,
              min: MIN_PERCENT,
              step: step ?? 'any',
            },
          }}
          endAdornment={percentAdornment}
          type="number"
          inputMode="decimal"
          value={displayValue}
        />
        <ValidationErrorMessage error={error} label={label} name={name} />
      </div>
    </div>
  );
};
